/**
 * Lightweight notification tone using the Web Audio API.
 * No audio asset is bundled — a short two-note chime is synthesized on demand.
 * Browsers require a prior user interaction before audio can play; if the
 * AudioContext is blocked, playback fails silently (never throws).
 */

let audioCtx = null;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx) audioCtx = new Ctx();
    return audioCtx;
  } catch {
    return null;
  }
}

// Collapse rapid duplicate triggers (e.g. the same message delivered to multiple
// socket rooms) into a single chime.
let lastPlayedAt = 0;
const MIN_INTERVAL_MS = 600;

/** Play a short, pleasant "new message" chime. Safe to call anytime. */
export function playNotificationSound() {
  const nowMs = Date.now();
  if (nowMs - lastPlayedAt < MIN_INTERVAL_MS) return;
  lastPlayedAt = nowMs;

  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const notes = [
      { freq: 880, start: 0, duration: 0.12 },
      { freq: 1175, start: 0.1, duration: 0.16 }
    ];

    notes.forEach(({ freq, start, duration }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const t0 = now + start;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    });
  } catch {
    // Ignore — audio is a non-critical enhancement.
  }
}
